import type { InventoryRow } from '@/infrastructure/FulfillmentAdminClient';
import { parseCsv } from '@/utils/marketingImportFile';
import { LOCATION_CODE_ERROR, LOCATION_REQUIRED_ERROR, isLocationCode, normalizeLocationCode } from './inventoryView';

/** The desk keeps shelf lists in Excel as often as in CSV. */
export const LOCATION_IMPORT_ACCEPT = '.csv,.xlsx,.xls';

/** Each move is its own request, so a file is kept to what one sitting can watch through. */
export const MAX_IMPORT_LINES = 1000;

/** One data row of the file, numbered as the sheet shows it (the header is row 1). */
export interface LocationImportLine {
  rowNo: number;
  sku: string;
  toLocation: string;
  /** Blank when the file names no source shelf. */
  fromLocation: string;
}

export interface LocationImportFile {
  lines: LocationImportLine[];
  error: string | null;
}

/** `superseded`: an earlier twin of a later row that asks for the same stock; the later row is the one sent. */
export type LocationImportStatus = 'move' | 'unchanged' | 'blocked' | 'superseded';

/** What the import will do with one file row; only `move` rows are sent. */
export interface LocationImportPlanLine extends LocationImportLine {
  status: LocationImportStatus;
  /** The shelf the stock leaves and the units it takes, on a `move` row. */
  sourceLocation?: string;
  qty?: number;
  reason?: string;
  /** Earlier rows this one replaced because they asked for the same stock [D15]. */
  replacesRows?: number[];
}

const HEADER_ALIASES: Record<'sku' | 'toLocation' | 'fromLocation', string[]> = {
  sku: ['sku'],
  toLocation: ['location', 'tolocation', 'newlocation'],
  fromLocation: ['fromlocation', 'currentlocation', 'from'],
};

const headerKey = (cell: string): string =>
  cell
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, '');

export const HEADER_ERROR = 'The first row must name the columns: SKU, Location, and optionally From Location.';

/**
 * Reads the desk's file into rows. Columns are found by header name, so notes beside them still read;
 * blank rows are dropped but every kept row keeps the number the sheet shows.
 */
export const parseLocationImport = (csv: string): LocationImportFile => {
  const [header, ...records] = parseCsv(csv.replace(/^\uFEFF/, ''));
  const names = (header ?? []).map(headerKey);
  const column = (key: keyof typeof HEADER_ALIASES) => names.findIndex((name) => HEADER_ALIASES[key].includes(name));
  const skuAt = column('sku');
  const toAt = column('toLocation');
  const fromAt = column('fromLocation');
  if (skuAt < 0 || toAt < 0) {
    return { lines: [], error: HEADER_ERROR };
  }
  const lines = records
    .map((cells, index) => ({
      rowNo: index + 2,
      sku: (cells[skuAt] ?? '').trim(),
      toLocation: normalizeLocationCode(cells[toAt] ?? ''),
      fromLocation: fromAt < 0 ? '' : normalizeLocationCode(cells[fromAt] ?? ''),
    }))
    .filter((line) => line.sku || line.toLocation || line.fromLocation);
  if (lines.length === 0) {
    return { lines: [], error: 'The file has no rows below its header.' };
  }
  if (lines.length > MAX_IMPORT_LINES) {
    return { lines: [], error: `The file has ${lines.length} rows; import at most ${MAX_IMPORT_LINES} at a time.` };
  }
  return { lines, error: null };
};

/**
 * A short fingerprint of the file's text (FNV-1a over its code units). A move's idempotency key is minted
 * per file row, so a key held for one file's row is never handed to another file that happens to repeat it.
 */
export const importFileHash = (csv: string): string => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < csv.length; index += 1) {
    hash ^= csv.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
};

/** The skus worth asking the server about: every distinct one the file names. */
export const importSkus = (lines: LocationImportLine[]): string[] => [
  ...new Set(lines.map((line) => line.sku).filter(Boolean)),
];

/** A sku with no stock has nothing to move; there is no home-location to assign at launch [D15]. */
export const NO_STOCK_REASON = 'No stock on any shelf, so there is nothing to move.';

const blocked = (line: LocationImportLine, reason: string): LocationImportPlanLine => ({
  ...line,
  status: 'blocked',
  reason,
});

const shelvesOf = (sku: string, inventory: InventoryRow[]): InventoryRow[] =>
  inventory.filter((row) => row.sku === sku && row.onHand > 0);

/**
 * The shelf a row would move from, as far as the stock can tell: the one the row names when stock sits
 * there, or the single shelf the sku sits on when the row names none. Undefined when it cannot be told.
 */
const sourceOf = (line: LocationImportLine, inventory: InventoryRow[]): string | undefined => {
  const shelves = shelvesOf(line.sku, inventory);
  if (line.fromLocation) {
    return shelves.some((row) => row.locationCode === line.fromLocation) ? line.fromLocation : undefined;
  }
  return shelves.length === 1 ? shelves[0]!.locationCode : undefined;
};

/** What one file row does on its own against the shelves the server holds now. */
const planRow = (line: LocationImportLine, inventory: InventoryRow[]): LocationImportPlanLine => {
  if (!line.sku) {
    return blocked(line, 'SKU is blank.');
  }
  if (!line.toLocation) {
    return blocked(line, LOCATION_REQUIRED_ERROR);
  }
  if (!isLocationCode(line.toLocation)) {
    return blocked(line, `Location: ${LOCATION_CODE_ERROR}`);
  }
  if (line.fromLocation && !isLocationCode(line.fromLocation)) {
    return blocked(line, `From Location: ${LOCATION_CODE_ERROR}`);
  }
  const shelves = shelvesOf(line.sku, inventory);
  if (shelves.length === 0) {
    return blocked(line, NO_STOCK_REASON);
  }
  let source: InventoryRow | undefined;
  if (line.fromLocation) {
    source = shelves.find((row) => row.locationCode === line.fromLocation);
    if (!source) {
      return blocked(line, `No stock of this SKU on ${line.fromLocation}.`);
    }
  } else if (shelves.length > 1) {
    // A sku on several shelves moves only from the shelf the file names [D15].
    const codes = shelves.map((row) => row.locationCode || 'Unassigned').join(', ');
    return blocked(
      line,
      `On ${shelves.length} shelves (${codes}). Add a From Location column to say which shelf moves.`,
    );
  } else {
    source = shelves[0]!;
  }
  if (!source.locationCode) {
    return blocked(line, 'This stock is not on a shelf, so there is nothing to move it from.');
  }
  if (source.locationCode === line.toLocation) {
    return {
      ...line,
      status: 'unchanged',
      sourceLocation: source.locationCode,
      reason: `Already on ${line.toLocation}.`,
    };
  }
  if (source.reserved > 0) {
    // The server only moves unreserved units, and the Location field moves the whole shelf.
    return blocked(
      line,
      `${source.reserved} reserved on ${source.locationCode}; a shelf with reserved stock cannot move.`,
    );
  }
  return { ...line, status: 'move', sourceLocation: source.locationCode, qty: source.onHand };
};

/**
 * Two rows asking for the same stock are one ask written twice, and the last row is taken as the one
 * meant [D15]: the earlier row stays in the preview as superseded and is not sent. Rows are twins when
 * they name the same sku and would move from the same shelf. The shelf is read as the row resolves it,
 * so a blank From on a sku sitting on one shelf pairs with a row that names that shelf outright; rows
 * naming different shelves are each kept.
 */
const lastRowWins = (plan: LocationImportPlanLine[], inventory: InventoryRow[]): LocationImportPlanLine[] => {
  const keys = plan.map((line) =>
    line.sku ? JSON.stringify([line.sku, sourceOf(line, inventory) ?? line.fromLocation]) : null,
  );
  const lastByKey = new Map<string, LocationImportPlanLine>();
  plan.forEach((line, index) => {
    const key = keys[index] ?? null;
    if (key !== null) {
      lastByKey.set(key, line);
    }
  });
  return plan.map((line, index) => {
    const key = keys[index] ?? null;
    const last = key === null ? undefined : lastByKey.get(key);
    if (!last || last === line) {
      const replaced =
        key === null ? [] : plan.filter((other, at) => other !== line && keys[at] === key).map((other) => other.rowNo);
      return replaced.length > 0 ? { ...line, replacesRows: replaced } : line;
    }
    const source = sourceOf(line, inventory);
    return {
      rowNo: line.rowNo,
      sku: line.sku,
      toLocation: line.toLocation,
      fromLocation: line.fromLocation,
      status: 'superseded',
      ...(source ? { sourceLocation: source } : {}),
      reason: `Row ${last.rowNo} lists this SKU${source ? ` from ${source}` : ''} again; the last row wins.`,
    };
  });
};

/**
 * What each file row does against the shelves the server holds now. A row moves stock only when it
 * points at exactly one shelf with stock and nothing reserved on it — the same move the Location
 * field makes — and every other row stays in the preview with the reason it is not applied. When two
 * rows ask for the same stock, the last one wins.
 */
export const planLocationImport = (lines: LocationImportLine[], inventory: InventoryRow[]): LocationImportPlanLine[] =>
  lastRowWins(
    lines.map((line) => planRow(line, inventory)),
    inventory,
  );

export interface LocationImportTally {
  move: number;
  unchanged: number;
  blocked: number;
  superseded: number;
}

export const tallyLocationImport = (plan: LocationImportPlanLine[]): LocationImportTally => ({
  move: plan.filter((line) => line.status === 'move').length,
  unchanged: plan.filter((line) => line.status === 'unchanged').length,
  blocked: plan.filter((line) => line.status === 'blocked').length,
  superseded: plan.filter((line) => line.status === 'superseded').length,
});

/** Two plans send the same moves when every row would do the same thing; anything else is re-shown first. */
export const samePlan = (a: LocationImportPlanLine[], b: LocationImportPlanLine[]): boolean =>
  a.length === b.length &&
  a.every((line, index) => {
    const other = b[index]!;
    return (
      line.rowNo === other.rowNo &&
      line.sku === other.sku &&
      line.toLocation === other.toLocation &&
      line.status === other.status &&
      line.sourceLocation === other.sourceLocation &&
      line.qty === other.qty &&
      line.reason === other.reason
    );
  });

/** Skus per inventory read: the server answers at most 500 shelf rows, and a full answer may have been cut short. */
export const SKU_CHUNK = 50;
export const TOO_MANY_SHELVES = 'Too many shelves to read at once.';
export const INVENTORY_READ_LIMIT = 500;

/**
 * Reads every shelf holding a sku the file names. A read that comes back at the server's row ceiling
 * may be missing shelves, and a plan built on it could move the wrong one, so it is refused instead.
 */
export const readImportInventory = async (
  listInventory: (params: { skus: string[]; limit: number }) => Promise<{ rows: InventoryRow[] }>,
  skus: string[],
): Promise<InventoryRow[]> => {
  const rows: InventoryRow[] = [];
  for (let start = 0; start < skus.length; start += SKU_CHUNK) {
    const result = await listInventory({ skus: skus.slice(start, start + SKU_CHUNK), limit: INVENTORY_READ_LIMIT });
    if (result.rows.length >= INVENTORY_READ_LIMIT) {
      throw new Error(TOO_MANY_SHELVES);
    }
    rows.push(...result.rows);
  }
  return rows;
};
